"use client";

import { readSqSidCookie } from "@analytics/lib/identity-cookie";
import { isIdentityGraphClientEnabled } from "@repo/identity";
import { useConsent } from "@shared/hooks/cookie-consent";
import { orpcClient } from "@shared/lib/orpc-client";
import { useCallback } from "react";

/**
 * Records a lifecycle signal on user action. Does not use {@link useSession} so it is safe
 * under {@link ConsentBanner} / {@link ClientProviders} before {@link SessionProvider}.
 */
export function useRecordIdentityLifecycleSignalOnAction(
	signal: string,
	storageKey: string,
): () => void {
	const { status, record } = useConsent();
	const analyticsConsentGranted =
		status === "decided" && Boolean(record?.cats.analytics);

	return useCallback(() => {
		if (!signal || !storageKey) {
			return;
		}
		if (!isIdentityGraphClientEnabled()) {
			return;
		}
		if (status === "unknown" || !analyticsConsentGranted) {
			return;
		}
		if (typeof window === "undefined") {
			return;
		}
		if (window.sessionStorage.getItem(storageKey) === "1") {
			return;
		}

		const sqid = readSqSidCookie()?.trim();
		if (!sqid) {
			return;
		}

		void orpcClient.identity
			.recordLifecycleSignal({ sqid, signal })
			.then(() => {
				window.sessionStorage.setItem(storageKey, "1");
			})
			.catch(() => {
				// Best-effort lifecycle signal.
			});
	}, [analyticsConsentGranted, signal, status, storageKey]);
}
